import { motion } from "framer-motion";
import {
  Smartphone,
  Bike,
  Store,
  Check,
  Sparkles,
  Package,
  Leaf,
} from "lucide-react";
import { StoreBadge } from "@/components/common/StoreBadge";
import { CtaDialog } from "@/components/common/CtaDialog";
import { PhoneMockup } from "@/components/common/PhoneMockup";
import { SectionReveal } from "@/components/common/SectionReveal";

const PRIMARY_BTN =
  "h-11 bg-halalgo-green-600 px-5 text-white shadow-lift-sm hover:bg-halalgo-green-700 active:scale-[0.98]";

const RIDER_FEATURES = [
  "Breathable Fabric",
  "High-visibility Reflective Strips",
  "Comfort Fit for long shifts",
];

const PACKAGING = ["Paper bags", "Food boxes", "Containers", "Drink cups"];

const Eyebrow = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-halalgo-kraft-200 bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-halalgo-green-800">
    <Icon className="h-3.5 w-3.5" /> {children}
  </span>
);

export default function Download() {
  return (
    <div data-testid="download-page">
      {/* ==================== SECTION 1 — CUSTOMERS (white) ==================== */}
      <section className="bg-white" data-testid="download-customers-section">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <Eyebrow icon={Smartphone}>For customers · The app</Eyebrow>
            <h1 className="mt-5 text-4xl font-bold tracking-tight text-halalgo-ink sm:text-5xl">
              Get Your Food Delivered.
            </h1>
            <p className="mt-4 text-lg font-semibold text-halalgo-green-800">
              100% HALAL. Trusted Food. Every Time.
            </p>
            <p className="mt-3 max-w-md text-halalgo-ink/70">
              Order from verified halal kitchens near you, delivered fresh in
              eco-friendly packaging. The Halal GO app is launching soon.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <StoreBadge store="apple" testId="download-appstore-badge" />
              <StoreBadge store="google" testId="download-playstore-badge" />
            </div>
            <p className="mt-3 flex items-center gap-1.5 text-xs text-halalgo-ink/55">
              <Sparkles className="h-3.5 w-3.5 text-halalgo-green-600" /> App Store &
              Google Play launches are coming soon.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, ease: "easeOut", delay: 0.1 }}
          >
            <PhoneMockup />
          </motion.div>
        </div>
      </section>

      {/* ==================== SECTION 2 — RIDERS (light green) ==================== */}
      <section className="bg-halalgo-green-50" data-testid="download-riders-section">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-24">
          <SectionReveal className="order-2 lg:order-1">
            <div className="overflow-hidden rounded-2xl border border-halalgo-green-200 bg-white p-3 shadow-lift-md">
              <img
                src="/brand/rider-uniform.png"
                alt="Halal GO official rider uniform: front, back and side views with uniform elements"
                className="w-full rounded-xl"
                loading="lazy"
              />
            </div>
          </SectionReveal>

          <SectionReveal delay={0.05} className="order-1 lg:order-2">
            <Eyebrow icon={Bike}>For riders · Join the fleet</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold tracking-tight text-halalgo-ink sm:text-4xl">
              Gear Up and Earn With Us.
            </h2>
            <p className="mt-4 max-w-md text-halalgo-ink/75">
              Ride with pride. Join a fleet that respects your values — and looks
              the part, too.
            </p>

            <ul className="mt-6 space-y-3">
              {RIDER_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3 text-halalgo-ink/85">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-halalgo-green-600 text-white">
                    <Check className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-medium leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <CtaDialog
                label="Apply as a Rider"
                icon={Bike}
                accentIcon={Bike}
                formType="rider"
                source="download-rider"
                title="Apply as a Rider"
                description="Join our founding rider fleet. Leave your details and our team will reach out with the next steps before launch."
                waitlistPrefix="download-rider-apply-wl"
                waitlistButtonLabel="Apply now"
                testId="download-rider-apply"
                buttonClassName={PRIMARY_BTN}
              />
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* ================= SECTION 3 — MERCHANT PARTNERS (light kraft) ================= */}
      <section className="bg-halalgo-kraft-50" data-testid="download-merchants-section">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-24">
          <SectionReveal>
            <Eyebrow icon={Store}>For merchant partners · The ecosystem</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold tracking-tight text-halalgo-ink sm:text-4xl">
              Serve Your Best. We’ll Handle the Rest.
            </h2>
            <p className="mt-4 max-w-md text-halalgo-ink/75">
              We provide our partners with premium, eco-friendly packaging options
              including paper bags, food boxes, containers, and drink cups to ensure
              food stays safe, trusted, and convenient.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {PACKAGING.map((p) => (
                <span
                  key={p}
                  className="inline-flex items-center gap-1.5 rounded-md border border-halalgo-kraft-200 bg-white px-3 py-1.5 text-sm font-medium text-halalgo-kraft-800"
                >
                  <Package className="h-3.5 w-3.5" /> {p}
                </span>
              ))}
            </div>

            <div className="mt-5 flex items-center gap-2 text-sm text-halalgo-ink/70">
              <Leaf className="h-4 w-4 text-halalgo-green-700" /> Kraft-first, plastic-light
              — kinder to the planet.
            </div>

            <div className="mt-8">
              <CtaDialog
                label="Register Your Restaurant"
                icon={Store}
                accentIcon={Store}
                formType="merchant"
                source="download-merchant"
                title="Register Your Restaurant"
                description="Join our Trusted Halal Partners program. Share your details and our partnerships team will get in touch before launch."
                waitlistPrefix="download-merchant-register-wl"
                waitlistButtonLabel="Register interest"
                testId="download-merchant-register"
                buttonClassName={PRIMARY_BTN}
              />
            </div>
          </SectionReveal>

          <SectionReveal delay={0.05}>
            <div className="overflow-hidden rounded-2xl border border-halalgo-kraft-200 bg-white p-3 shadow-lift-md">
              <img
                src="/brand/food-packaging.png"
                alt="Halal GO eco-friendly food packaging: paper bag, food box, container and drink cup"
                className="w-full rounded-xl"
                loading="lazy"
              />
            </div>
          </SectionReveal>
        </div>
      </section>
    </div>
  );
}
